import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAbility } from '../../contexts/AbilityContext';
import {
  ArrowRight,
  Bug,
  Calendar,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  CreditCard,
  FileText,
  Gift,
  Handshake,
  LayoutDashboard,
  LayoutGrid,
  Lightbulb,
  MessageSquare,
  Phone,
  Send,
  Settings,
  Sparkles,
  Target,
  User,
  UserPlus,
  Users,
  UsersRound,
  Video,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { vendorApi } from '../../store/vendorApi';
import subscriptionsData from '../../data/subscriptions.json';

const TEAM_MEMBER_ROLES = ['team_sales', 'team_customer_success', 'team_account_manager', 'team_manager', 'team_marketing_manager'];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const NavSkeleton: React.FC<{ width?: string }> = ({ width = 'w-24' }) => (
  <div className="flex items-center px-4 py-2">
    <div className="w-4 h-4 mr-3 rounded bg-white/15 animate-pulse flex-shrink-0" />
    <div className={`h-3.5 rounded bg-white/15 animate-pulse ${width}`} />
  </div>
);

const SidebarSkeleton: React.FC = () => (
  <div className="space-y-1.5">
    <NavSkeleton width="w-20" />
    <NavSkeleton width="w-24" />
    <NavSkeleton width="w-20" />
    <NavSkeleton width="w-28" />
    <NavSkeleton width="w-16" />
    <NavSkeleton width="w-20" />
    <NavSkeleton width="w-28" />
  </div>
);

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const ability = useAbility();
  const location = useLocation();
  const navigate = useNavigate();
  const [promotersMenuOpen, setPromotersMenuOpen] = React.useState(false);

  const { isLoading: permissionsLoading } = vendorApi.useGetPermissionsQuery(undefined, {
    skip: !isAuthenticated,
  });

  const isTeamMember = user?.role && TEAM_MEMBER_ROLES.includes(user.role);

  const currentProduct = subscriptionsData.products.find(p => p.id === subscriptionsData.currentSubscription.productId);

  React.useEffect(() => {
    if (location.pathname.includes('/reviews') ||
        location.pathname.includes('/testimonials') ||
        location.pathname.includes('/use-cases') ||
        location.pathname.includes('/referrals') ||
        location.pathname.includes('/1to1-calls') ||
        location.pathname.includes('/survey') ||
        location.pathname.includes('/promoter')) {
      setPromotersMenuOpen(true);
    }
  }, [location.pathname]);

  const promotersSubItems = [
    { path: '/survey', icon: Send, label: t('nav.survey') },
    { path: '/reviews', icon: MessageSquare, label: t('nav.reviews') },
    { path: '/testimonials', icon: Video, label: t('nav.testimonials') },
    { path: '/use-cases', icon: Lightbulb, label: t('nav.useCases') },
    { path: '/1to1-calls', icon: Phone, label: t('nav.oneToOne') },
    { path: '/referrals', icon: UserPlus, label: t('nav.referrals') },
  ];

  const closeMobileMenu = () => {
    if (window.innerWidth < 768) onClose();
  };

  const handleSubmitBug = () => {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'B', ctrlKey: true, shiftKey: true, bubbles: true }));
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center px-4 py-2 text-white rounded-lg transition-all duration-200 text-sm ${
      isActive
        ? 'bg-white bg-opacity-25 font-semibold shadow-lg border-l-4 border-white'
        : 'hover:bg-white hover:bg-opacity-10'
    }`;

  return (
    <>
      <div className={`sidebar ${isOpen ? 'mobile-open' : ''}`}>
        <div className="p-4 flex flex-col h-full min-h-0">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0 mb-4">
            <img
              src="/logo-clareefai-white.png"
              alt="Clareefai"
              className="h-9 w-auto"
            />
          </div>

          <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden space-y-0.5 pr-1">
            {permissionsLoading ? (
              <SidebarSkeleton />
            ) : (
              <>
                {/* Dashboard — always visible */}
                <NavLink to="/dashboard" onClick={closeMobileMenu} className={navLinkClass}>
                  <LayoutDashboard className="w-4 h-4 mr-3 flex-shrink-0" />
                  {t('nav.dashboard')}
                </NavLink>

                {/* Promoters menu with submenu — always visible */}
                <div className="space-y-0.5">
                  <NavLink
                    to="/promoters"
                    onClick={() => {
                      setPromotersMenuOpen(!promotersMenuOpen);
                      closeMobileMenu();
                    }}
                    className={({ isActive }) =>
                      `flex items-center justify-between w-full px-4 py-2 text-white rounded-lg transition-all duration-200 text-sm ${
                        isActive || location.pathname.includes('/reviews') || location.pathname.includes('/testimonials') || location.pathname.includes('/use-cases') || location.pathname.includes('/referrals') || location.pathname.includes('/promoter') || location.pathname.includes('/survey')
                          ? 'bg-white bg-opacity-25 font-semibold shadow-lg border-l-4 border-white'
                          : 'hover:bg-white hover:bg-opacity-10'
                      }`
                    }
                  >
                    <div className="flex items-center min-w-0">
                      <Users className="w-4 h-4 mr-3 flex-shrink-0" />
                      <span className="truncate">{t('nav.promoters')}</span>
                    </div>
                    {promotersMenuOpen ? (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 flex-shrink-0" />
                    )}
                  </NavLink>

                  {promotersMenuOpen && (
                    <div className="ml-4 mt-0.5 space-y-0.5">
                      {promotersSubItems.map((item) => (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          onClick={closeMobileMenu}
                          className={({ isActive }) =>
                            `flex items-center px-4 py-1.5 text-white rounded-lg transition-all duration-200 text-sm ${
                              isActive
                                ? 'bg-white bg-opacity-30 font-semibold shadow-md border-l-2 border-white'
                                : 'hover:bg-white hover:bg-opacity-10 text-white text-opacity-90'
                            }`
                          }
                        >
                          <item.icon className="w-4 h-4 mr-2.5 flex-shrink-0" />
                          <span className="truncate">{item.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>

                {/* Prospects — always visible (all roles have add_prospect) */}
                <NavLink to="/prospects" onClick={closeMobileMenu} className={navLinkClass}>
                  <Target className="w-4 h-4 mr-3 flex-shrink-0" />
                  {t('nav.prospects')}
                </NavLink>

                {/* Meetings — always visible */}
                <NavLink to="/meetings" onClick={closeMobileMenu} className={navLinkClass}>
                  <Calendar className="w-4 h-4 mr-3 flex-shrink-0" />
                  {t('nav.oneToOneCalendar')}
                </NavLink>

                {/* Rewards — visible if user can create, manage, or read Reward */}
                {(ability.can('create', 'Reward') || ability.can('read', 'Reward')) && (
                  <NavLink to="/rewards" onClick={closeMobileMenu} className={navLinkClass}>
                    <Gift className="w-4 h-4 mr-3 flex-shrink-0" />
                    {t('nav.rewards')}
                  </NavLink>
                )}

                {/* My Page — visible to all vendor users and team members; edit affordances inside are gated by `update Page` */}
                <NavLink to="/my-page" onClick={closeMobileMenu} className={navLinkClass}>
                  <FileText className="w-4 h-4 mr-3 flex-shrink-0" />
                  {t('nav.myPage')}
                </NavLink>

                {/* Widget Management — embeddable widgets (reviews + backoffice). Visible to vendors + team. */}
                <NavLink to="/widget-management" onClick={closeMobileMenu} className={navLinkClass}>
                  <LayoutGrid className="w-4 h-4 mr-3 flex-shrink-0" />
                  {t('nav.widgetManagement', 'Widget Management')}
                </NavLink>

                {/* Deal Rooms — curated shareable proof pages for prospects. Visible to vendors + team. */}
                <NavLink to="/deal-rooms" onClick={closeMobileMenu} className={navLinkClass}>
                  <Handshake className="w-4 h-4 mr-3 flex-shrink-0" />
                  {t('nav.dealRooms', 'Deal Rooms')}
                </NavLink>

                {/* Team Members — only if user can manage or read TeamMember */}
                {(ability.can('manage', 'TeamMember') || ability.can('read', 'TeamMember')) && (
                  <NavLink to="/teams-sales" onClick={closeMobileMenu} className={navLinkClass}>
                    <UsersRound className="w-4 h-4 mr-3 flex-shrink-0" />
                    {t('nav.teamMembers')}
                  </NavLink>
                )}
              </>
            )}
          </nav>

          {/* Bottom section — Profile (team member) or Subscription box with Settings (vendor) */}
          <div className="flex-shrink-0 mt-3 pt-3 border-t border-white/20">
            {permissionsLoading ? (
              <NavSkeleton width="w-20" />
            ) : (
              <>
                {isTeamMember ? (
                  /* Team member bottom links — assignments + profile */
                  <div className="space-y-0.5">
                    <NavLink
                      to="/my-assignments"
                      onClick={closeMobileMenu}
                      className={navLinkClass}
                    >
                      <ClipboardList className="w-4 h-4 mr-3 flex-shrink-0" />
                      <span className="truncate">{t('nav.myAssignments')}</span>
                    </NavLink>
                    <NavLink
                      to="/team-settings"
                      onClick={closeMobileMenu}
                      className={navLinkClass}
                    >
                      <User className="w-4 h-4 mr-3 flex-shrink-0" />
                      <span className="truncate">{t('nav.mySettings')}</span>
                    </NavLink>
                  </div>
                ) : (
                  /* Vendor bottom card — Settings + Subscription */
                  <div className="bg-gradient-to-br from-orange-500/90 to-orange-600/90 backdrop-blur-sm rounded-xl p-3 border border-orange-400/50 shadow-lg">
                    {currentProduct && (
                      <div className="flex items-center space-x-3 mb-2.5">
                        <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Sparkles className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white/80 uppercase font-semibold leading-tight">{t('nav.currentPlan')}</p>
                          <p className="text-sm text-white font-bold truncate leading-tight">{currentProduct.name.replace('ClareefAI ', '').replace('Clareefai ', '')}</p>
                        </div>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <button
                        onClick={() => {
                          navigate('/settings');
                          closeMobileMenu();
                        }}
                        className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-xs font-medium transition-colors"
                      >
                        <Settings className="w-4 h-4 flex-shrink-0" />
                        <span>{t('nav.settings')}</span>
                      </button>
                      {ability.can('manage', 'Subscription') && (
                        <button
                          onClick={() => {
                            navigate('/my-subscription');
                            closeMobileMenu();
                          }}
                          className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-white/25 hover:bg-white/35 text-white rounded-lg text-xs font-semibold transition-colors"
                        >
                          <CreditCard className="w-4 h-4 flex-shrink-0" />
                          <span>{t('nav.manageSubscription')}</span>
                          <ArrowRight className="w-4 h-4 flex-shrink-0" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {/* Submit a Bug — visible to all roles (vendors + team members) */}
                <button
                  onClick={handleSubmitBug}
                  className="mt-1 flex items-center px-4 py-2 text-white rounded-lg transition-all duration-200 text-sm hover:bg-white hover:bg-opacity-10 w-full"
                >
                  <Bug className="w-4 h-4 mr-3 flex-shrink-0" />
                  {t('nav.submitBug')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-999 md:hidden"
          onClick={onClose}
        />
      )}
    </>
  );
};

export default Sidebar;
